import { useState, useEffect, useRef } from "react";
import { Tooltip } from "~/components/Tooltip";
import { cn } from "~/lib/utils";
import { useUser } from "~/modules/user/hooks/useUser";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// // // //

export interface TabSelectOption {
    label: string;
    value: string;
    count?: number;
    tooltip?: string;
    testID?: string;
    loginRequired?: boolean;
}

interface Props {
    title?: string | null;
    options: Array<TabSelectOption>;
    value?: string;
    onValueChange: (value: string) => void;
    onValueChangeLoginRequired?: (value: string) => void;
    disabled?: boolean;
}

export function TabSelectV2(props: Props) {
    const {
        title,
        options,
        value,
        onValueChange,
        onValueChangeLoginRequired,
        disabled,
    } = props;

    const { isLoggedIn } = useUser();

    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());

    const [indicatorStyle, setIndicatorStyle] = useState({
        left: 0,
        width: 0,
    });

    const currentValue = value || options[0]?.value;

    useEffect(() => {
        const updateIndicator = () => {
            const targetEl = buttonRefs.current.get(currentValue);
            const containerEl = containerRef.current;

            if (targetEl && containerEl) {
                const containerRect = containerEl.getBoundingClientRect();
                const targetRect = targetEl.getBoundingClientRect();

                setIndicatorStyle({
                    left: targetRect.left - containerRect.left,
                    width: targetRect.width,
                });
            }
        };

        updateIndicator();
        window.addEventListener("resize", updateIndicator);
        const timeoutId = setTimeout(updateIndicator, 50);

        return () => {
            window.removeEventListener("resize", updateIndicator);
            clearTimeout(timeoutId);
        };
    }, [currentValue, options]);

    return (
        <div
            className={cn(
                "flex shrink-0 items-center gap-2 select-none",
                title ? "gap-2" : "",
            )}
        >
            {title && (
                <h2 className="text-sm font-semibold text-foreground whitespace-nowrap">
                    {title}
                </h2>
            )}
            <div
                ref={containerRef}
                className="relative inline-flex w-max shrink-0 gap-1.5 rounded-lg bg-muted p-1"
            >
                {/* Sliding indicator */}
                {indicatorStyle.width > 0 && (
                    <div
                        className="absolute top-1 bottom-1 bg-card ring-1 ring-slate-400/60 rounded-md shadow-sm transition-all duration-200 ease-in-out"
                        style={{
                            left: `${indicatorStyle.left}px`,
                            width: `${indicatorStyle.width}px`,
                        }}
                    />
                )}

                {options.map((option) => {
                    const isActive = currentValue === option.value;
                    const isLoginRequired =
                        !!option.loginRequired && !isLoggedIn;

                    const buttonContent = (
                        <button
                            key={option.value}
                            data-testid={option.testID}
                            ref={(el) => {
                                buttonRefs.current.set(option.value, el);
                            }}
                            onClick={() => {
                                if (disabled) return;

                                // Invoke props.onValueChangeLoginRequired if
                                // the option is loginRequired and the user isn't logged in
                                if (isLoginRequired) {
                                    if (onValueChangeLoginRequired) {
                                        onValueChangeLoginRequired(
                                            option.value,
                                        );
                                    }
                                    return;
                                }

                                onValueChange(option.value);
                            }}
                            disabled={disabled}
                            className={cn(
                                "relative z-10 flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium duration-200 outline-none hover:ring-1 hover:ring-slate-400/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                                {
                                    "bg-card text-foreground/80": isActive,
                                    "text-foreground/50": !isActive,
                                    "hover:text-foreground":
                                        !disabled && !isActive,
                                    "opacity-50 cursor-not-allowed": disabled,
                                },
                            )}
                        >
                            {isLoginRequired && (
                                <FontAwesomeIcon
                                    icon={faLock}
                                    className="h-2.5 w-2.5"
                                />
                            )}
                            <span>{option.label}</span>
                            {option.count !== undefined && (
                                <span
                                    className={cn(
                                        "px-1.5 py-0.5 rounded text-[10px] leading-none",
                                        isActive
                                            ? "bg-slate-400/50 text-foreground/60"
                                            : "bg-slate-400/50 text-foreground/50",
                                    )}
                                >
                                    {option.count}
                                </span>
                            )}
                        </button>
                    );

                    if (option.tooltip) {
                        return (
                            <Tooltip
                                key={option.value}
                                placement="top"
                                tooltipContent={
                                    <div className="w-max px-3 py-2 max-w-xs text-sm">
                                        {option.tooltip}
                                    </div>
                                }
                            >
                                {({ setTriggerRef }) => (
                                    <span ref={setTriggerRef} className="flex">
                                        {buttonContent}
                                    </span>
                                )}
                            </Tooltip>
                        );
                    }

                    return buttonContent;
                })}
            </div>
        </div>
    );
}
